import { useState } from 'react';
import {
  Paper,
  Title,
  Text,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Tabs,
  Group,
  Badge,
  Switch,
  Divider,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconShield, IconSettings, IconDatabase, IconMail, IconUsers } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { showSuccess } from '../../lib/notifications';
import { useAuth } from '../../contexts/AuthContext';
import { PageTransition } from '../../components/PageTransition';

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface SystemSettings {
  maintenanceMode: boolean;
  allowUserRegistration: boolean;
  sessionTimeout: number;
  auditLogRetention: number;
}

export function AdminSettingsPage() {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>('profile');

  const profileForm = useForm<ProfileForm>({
    initialValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
    },
    validate: {
      firstName: (value) => (!value ? 'First name is required' : null),
      lastName: (value) => (!value ? 'Last name is required' : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
    },
  });

  const passwordForm = useForm<PasswordForm>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (value) =>
        !value ? 'Current password is required' : null,
      newPassword: (value) =>
        value.length < 8
          ? 'Password must be at least 8 characters'
          : null,
      confirmPassword: (value, values) =>
        value !== values.newPassword ? 'Passwords do not match' : null,
    },
  });

  const systemForm = useForm<SystemSettings>({
    initialValues: {
      maintenanceMode: false,
      allowUserRegistration: true,
      sessionTimeout: 60,
      auditLogRetention: 90,
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (data: ProfileForm) => {
      // TODO: Implement update profile API
      return Promise.resolve();
    },
    onSuccess: () => {
      showSuccess('Profile updated successfully');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to update profile');
    },
  });

  const updatePassword = useMutation({
    mutationFn: async (data: PasswordForm) => {
      // TODO: Implement update password API
      return Promise.resolve();
    },
    onSuccess: () => {
      passwordForm.reset();
      showSuccess('Password updated successfully');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to update password');
    },
  });

  const updateSystemSettings = useMutation({
    mutationFn: async (data: SystemSettings) => {
      // TODO: Implement system settings API
      return Promise.resolve();
    },
    onSuccess: () => {
      showSuccess('System settings updated successfully');
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to update system settings');
    },
  });

  return (
    <PageTransition>
        <PageHeader
          title="System Settings"
          description="Manage system-wide settings and administrative configurations"
        >
          <Group spacing="sm">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<IconUsers size={16} />}
              onClick={() => window.location.href = '/admin/users'}
            >
              User Management
            </Button>
          </Group>
        </PageHeader>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            title="Error"
            onClose={() => setError('')}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        <Tabs value={activeTab} onTabChange={setActiveTab} mb="md">
          <Tabs.List sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Tabs.Tab value="profile" icon={<IconSettings size={14} />}>
              Profile Settings
            </Tabs.Tab>
            <Tabs.Tab value="security" icon={<IconShield size={14} />}>
              Security
            </Tabs.Tab>
            <Tabs.Tab value="system" icon={<IconDatabase size={14} />}>
              System Settings
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile">
            <Paper withBorder p="md" radius="md" mt="md">
              <Title order={3}>Profile Settings</Title>
              <Text color="dimmed" size="sm" mb="lg">
                Update your personal information
              </Text>

              <form onSubmit={profileForm.onSubmit((values) => updateProfile.mutate(values))}>
                <Stack>
                  <TextInput
                    label="First Name"
                    placeholder="John"
                    required
                    {...profileForm.getInputProps('firstName')}
                  />
                  <TextInput
                    label="Last Name"
                    placeholder="Doe"
                    required
                    {...profileForm.getInputProps('lastName')}
                  />
                  <TextInput
                    label="Email"
                    placeholder="john@example.com"
                    required
                    disabled
                    {...profileForm.getInputProps('email')}
                  />
                  <Button
                    type="submit"
                    loading={updateProfile.isLoading}
                  >
                    Update Profile
                  </Button>
                </Stack>
              </form>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="security">
            <Paper withBorder p="md" radius="md" mt="md">
              <Title order={3}>Change Password</Title>
              <Text color="dimmed" size="sm" mb="lg">
                Update your password
              </Text>

              <form onSubmit={passwordForm.onSubmit((values) => updatePassword.mutate(values))}>
                <Stack>
                  <PasswordInput
                    label="Current Password"
                    required
                    {...passwordForm.getInputProps('currentPassword')}
                  />
                  <PasswordInput
                    label="New Password"
                    required
                    {...passwordForm.getInputProps('newPassword')}
                  />
                  <PasswordInput
                    label="Confirm New Password"
                    required
                    {...passwordForm.getInputProps('confirmPassword')}
                  />
                  <Button
                    type="submit"
                    loading={updatePassword.isLoading}
                  >
                    Change Password
                  </Button>
                </Stack>
              </form>
            </Paper>
          </Tabs.Panel>

          <Tabs.Panel value="system">
            <Stack spacing="md" mt="md">
              <Paper withBorder p="md" radius="md">
                <Title order={3}>System Configuration</Title>
                <Text color="dimmed" size="sm" mb="lg">
                  Configure system-wide settings
                </Text>

                <form onSubmit={systemForm.onSubmit((values) => updateSystemSettings.mutate(values))}>
                  <Stack spacing="md">
                    <Group position="apart">
                      <div>
                        <Text weight={500}>Maintenance Mode</Text>
                        <Text size="sm" color="dimmed">
                          Enable maintenance mode to restrict access during updates
                        </Text>
                      </div>
                      <Switch
                        {...systemForm.getInputProps('maintenanceMode', { type: 'checkbox' })}
                      />
                    </Group>

                    <Divider />

                    <Group position="apart">
                      <div>
                        <Text weight={500}>Allow User Registration</Text>
                        <Text size="sm" color="dimmed">
                          Allow new users to register accounts
                        </Text>
                      </div>
                      <Switch
                        {...systemForm.getInputProps('allowUserRegistration', { type: 'checkbox' })}
                      />
                    </Group>

                    <Divider />

                    <TextInput
                      label="Session Timeout (minutes)"
                      description="How long users stay logged in without activity"
                      type="number"
                      min={1}
                      max={1440}
                      {...systemForm.getInputProps('sessionTimeout')}
                    />

                    <TextInput
                      label="Audit Log Retention (days)"
                      description="How long to keep audit logs before deletion"
                      type="number"
                      min={1}
                      max={365}
                      {...systemForm.getInputProps('auditLogRetention')}
                    />

                    <Button
                      type="submit"
                      loading={updateSystemSettings.isLoading}
                      color="thanos"
                    >
                      Update System Settings
                    </Button>
                  </Stack>
                </form>
              </Paper>

              <Paper withBorder p="md" radius="md">
                <Title order={3}>Database Information</Title>
                <Text color="dimmed" size="sm" mb="md">
                  System database status and statistics
                </Text>

                <Stack spacing="sm">
                  <Group position="apart">
                    <Text>Database Status</Text>
                    <Badge color="green">Connected</Badge>
                  </Group>
                  <Group position="apart">
                    <Text>Total Users</Text>
                    <Badge color="blue">24</Badge>
                  </Group>
                  <Group position="apart">
                    <Text>Active Services</Text>
                    <Badge color="teal">8</Badge>
                  </Group>
                  <Group position="apart">
                    <Text>Pending Invitations</Text>
                    <Badge color="yellow">3</Badge>
                  </Group>
                </Stack>
              </Paper>

              <Paper withBorder p="md" radius="md">
                <Title order={3}>Email Configuration</Title>
                <Text color="dimmed" size="sm" mb="md">
                  Email service settings for notifications and invitations
                </Text>

                <Alert color="yellow" icon={<IconMail size={16} />}>
                  <Text size="sm">
                    Email configuration is managed through environment variables.
                    Contact your system administrator to modify email settings.
                  </Text>
                </Alert>
              </Paper>
            </Stack>
          </Tabs.Panel>
        </Tabs>
    </PageTransition>
  );
}
